/* NewEden2.0 — theme.js
   Core utilities + global UI: header, off-canvas drawers, mobile nav,
   mega-menu a11y, quantity stepper custom element, global event bus.
   Vanilla JS, no dependencies. */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */
  var NE = (window.NewEden = window.NewEden || {});
  NE.routes = NE.routes || {};
  NE.strings = NE.strings || {};
  NE.cart = NE.cart || {};

  if (document.documentElement.lang && document.documentElement.lang.toLowerCase().indexOf('en') === 0) {
    document.querySelectorAll('a[href^="/"]').forEach(function (link) {
      var href = link.getAttribute('href');
      if (!href || href === '/en' || href.indexOf('/en/') === 0 || href.indexOf('//') === 0 || href.indexOf('/cdn/') === 0 || href.indexOf('/localization') === 0) return;
      link.setAttribute('href', '/en' + (href === '/' ? '' : href));
    });
  }

  // Simple pub/sub so modules (cart, search, picker) can talk without coupling.
  var bus = {};
  NE.on = function (evt, cb) { (bus[evt] = bus[evt] || []).push(cb); };
  NE.emit = function (evt, data) { (bus[evt] || []).forEach(function (cb) { try { cb(data); } catch (e) { console.error(e); } }); };

  NE.PUB = {
    cartUpdate: 'cart:update',
    drawerOpen: 'drawer:open',
    drawerClose: 'drawer:close',
    variantChange: 'variant:change'
  };

  // Money formatting using shop.money_format string from theme.liquid.
  NE.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var fmt = format || NE.moneyFormat || '${{amount}}';
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultTo(num, prec, thousands, decimal) {
      prec = isNaN(prec) ? 2 : prec;
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      var n = (parseFloat(cents || 0) / 100.0).toFixed(prec);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = fmt.match(placeholderRegex);
    var token = match ? match[1] : 'amount';
    switch (token) {
      case 'amount': value = defaultTo(cents, 2); break;
      case 'amount_no_decimals': value = defaultTo(cents, 0); break;
      case 'amount_with_comma_separator': value = defaultTo(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = defaultTo(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = defaultTo(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = defaultTo(cents, 0, ' ', ''); break;
      default: value = defaultTo(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  };

  NE.fetchConfig = function (type) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    };
  };

  NE.debounce = function (fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  };

  NE.trapFocus = function (container, focusEl) {
    var focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return function () {};
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    (focusEl || first).focus();
    function onKey(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    return function () { container.removeEventListener('keydown', onKey); };
  };

  /* ------------------------------------------------------------- drawers */
  // Off-canvas controller for self-contained [data-drawer="name"] elements.
  // Each drawer carries its own .drawer__overlay; we just toggle .is-open.
  var openDrawers = {};

  NE.openDrawer = function (name, trigger) {
    var el = document.querySelector('[data-drawer="' + name + '"]');
    if (!el) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    var release = NE.trapFocus(el, el.querySelector('[data-drawer-focus], [data-drawer-close]'));
    openDrawers[name] = { el: el, release: release, trigger: trigger || document.activeElement };
    NE.emit(NE.PUB.drawerOpen, name);
  };

  NE.closeDrawer = function (name) {
    var d = openDrawers[name];
    if (!d) return;
    d.el.classList.remove('is-open');
    d.el.setAttribute('aria-hidden', 'true');
    if (d.release) d.release();
    if (d.trigger && d.trigger.focus) d.trigger.focus();
    delete openDrawers[name];
    if (!Object.keys(openDrawers).length) document.body.classList.remove('drawer-open');
    NE.emit(NE.PUB.drawerClose, name);
  };

  NE.closeAllDrawers = function () {
    Object.keys(openDrawers).forEach(function (n) { NE.closeDrawer(n); });
  };

  // Wire up [data-drawer-trigger="name"] and [data-drawer-close]
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-drawer-trigger]');
    if (trigger) {
      e.preventDefault();
      var name = trigger.getAttribute('data-drawer-trigger');
      if (openDrawers[name]) NE.closeDrawer(name); else NE.openDrawer(name, trigger);
      return;
    }
    var closeBtn = e.target.closest('[data-drawer-close]');
    if (closeBtn) {
      e.preventDefault();
      var dr = closeBtn.closest('[data-drawer]');
      if (dr) NE.closeDrawer(dr.getAttribute('data-drawer'));
    }
  });

  /* -------------------------------------------------------- sticky header */
  var header = document.querySelector('[data-header]');

  if (header) {
    var lastY = 0;
    var heroEl = null;

    var findHero = function () {
      if (!header.hasAttribute('data-transparent-home')) return null;
      return document.querySelector('.content-for-layout .hero') || document.querySelector('main .hero');
    };

    var setHeaderHeight = function () {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    };

    var updateHeaderScrollState = function () {
      heroEl = heroEl || findHero();
      if (heroEl) {
        var pastHero = heroEl.getBoundingClientRect().bottom <= 0;
        header.classList.toggle('is-scrolled', pastHero);
      } else {
        header.classList.toggle('is-scrolled', (window.scrollY || window.pageYOffset) > 40);
      }
    };

    setHeaderHeight();
    window.addEventListener('resize', NE.debounce(function () {
      heroEl = findHero();
      setHeaderHeight();
      updateHeaderScrollState();
    }, 100));

    var onScroll = function () {
      var y = window.scrollY || window.pageYOffset;
      updateHeaderScrollState();
      if (header.hasAttribute('data-sticky-hide')) {
        if (y > lastY && y > 200) header.classList.add('is-hidden');
        else header.classList.remove('is-hidden');
      }
      lastY = y;
    };
    window.addEventListener('scroll', NE.debounce(onScroll, 10), { passive: true });
    onScroll();
  }

  /* ------------------------------------------------- mega-menu hover + a11y */
  document.querySelectorAll('[data-mega-menu]').forEach(function (menu) {
    var items = menu.querySelectorAll('[data-mega-item]');
    var closeTimer;
    var navHoverTimer;

    var syncHeaderState = function () {
      if (!header) return;
      var open = menu.querySelector('.has-mega.is-open');
      header.classList.toggle('is-mega-open', !!open);
    };

    var closeAll = function () {
      items.forEach(function (i) { i.classList.remove('is-open'); });
      syncHeaderState();
    };

    menu.querySelectorAll('.header__nav-item').forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        clearTimeout(navHoverTimer);
        if (header) header.classList.add('is-nav-hover');
      });
      item.addEventListener('mouseleave', function () {
        navHoverTimer = setTimeout(function () {
          if (header) header.classList.remove('is-nav-hover');
        }, 120);
      });
    });

    items.forEach(function (item) {
      var panel = item.querySelector('[data-mega-panel]');
      var link = item.querySelector('a');
      if (!panel) return;

      item.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        items.forEach(function (i) { i.classList.remove('is-open'); });
        item.classList.add('is-open');
        syncHeaderState();
      });

      item.addEventListener('mouseleave', function () {
        closeTimer = setTimeout(function () {
          item.classList.remove('is-open');
          syncHeaderState();
        }, 120);
      });

      panel.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        item.classList.add('is-open');
        syncHeaderState();
      });

      panel.addEventListener('mouseleave', function () {
        closeTimer = setTimeout(function () {
          item.classList.remove('is-open');
          syncHeaderState();
        }, 120);
      });

      if (link) {
        link.addEventListener('focus', function () {
          closeAll();
          item.classList.add('is-open');
          syncHeaderState();
        });
      }
    });

    menu.addEventListener('focusout', function (e) {
      if (!menu.contains(e.relatedTarget)) closeAll();
    });
  });

  /* ------------------------------------------ mobile accordion sub-menus */
  document.querySelectorAll('[data-mobile-submenu-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var parent = btn.closest('[data-mobile-item], [data-mobile-subitem]');
      if (parent) parent.classList.toggle('is-open');
    });
  });

  /* ---------------------------------------- <quantity-input> custom el */
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.addEventListener('click', this.onClick.bind(this));
      }
      onClick(e) {
        var btn = e.target.closest('button');
        if (!btn) return;
        e.preventDefault();
        var step = parseInt(this.input.step || '1', 10) || 1;
        var val = parseInt(this.input.value || '0', 10) || 0;
        var min = this.input.min !== '' ? parseInt(this.input.min, 10) : 1;
        var max = this.input.max !== '' ? parseInt(this.input.max, 10) : Infinity;
        if (btn.name === 'plus') val = Math.min(max, val + step);
        else val = Math.max(min, val - step);
        if (String(val) !== this.input.value) {
          this.input.value = val;
          this.input.dispatchEvent(this.changeEvent);
        }
      }
    });
  }

  /* ------------------------------------------------ generic accordions */
  // FAQ / product accordion (details handled natively; custom toggles here)
  document.querySelectorAll('[data-accordion-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var item = btn.closest('[data-accordion-item]');
      if (!item) return;
      var group = btn.closest('[data-accordion-single]');
      if (group) {
        group.querySelectorAll('[data-accordion-item].is-open').forEach(function (i) {
          if (i !== item) {
            i.classList.remove('is-open');
            var otherBtn = i.querySelector('[data-accordion-toggle]');
            if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
          }
        });
      }
      item.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', item.classList.contains('is-open'));
    });
  });

  /* ------------------------------------------------------- tabs (generic) */
  document.querySelectorAll('[data-tabs]').forEach(function (tabs) {
    var triggers = tabs.querySelectorAll('[data-tab-trigger]');
    var panels = tabs.querySelectorAll('[data-tab-panel]');
    triggers.forEach(function (trig) {
      trig.addEventListener('click', function () {
        var id = trig.getAttribute('data-tab-trigger');
        triggers.forEach(function (t) { t.classList.toggle('is-active', t === trig); });
        panels.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-tab-panel') === id); });
      });
    });
  });

  /* ----------------------------------------------- countdown timers */
  document.querySelectorAll('[data-countdown]').forEach(function (el) {
    var deadline = el.getAttribute('data-deadline') || el.getAttribute('data-countdown');
    var end = new Date(deadline).getTime();
    if (isNaN(end)) return;
    var dEl = el.querySelector('[data-cd-days]');
    var hEl = el.querySelector('[data-cd-hours]');
    var mEl = el.querySelector('[data-cd-mins]');
    var sEl = el.querySelector('[data-cd-secs]');
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function tick() {
      var diff = end - Date.now();
      if (diff <= 0) {
        el.classList.add('is-expired');
        var exp = el.getAttribute('data-expired-text') || 'Aktion beendet';
        if (dEl) dEl.textContent = '00';
        if (hEl) hEl.textContent = '00';
        if (mEl) mEl.textContent = '00';
        if (sEl) sEl.textContent = '00';
        return;
      }
      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff % 86400000) / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      if (dEl) dEl.textContent = pad(d);
      if (hEl) hEl.textContent = pad(h);
      if (mEl) mEl.textContent = pad(m);
      if (sEl) sEl.textContent = pad(s);
      setTimeout(tick, 1000);
    }
    tick();
  });

  /* ------------------------------------------------ size guide modal */
  var sizeGuideCloseTimer;
  var sizeGuideTrigger;

  function getSizeGuideModal() {
    return document.querySelector('[data-size-guide-modal]');
  }

  function buildSizeGuideTable(sizeGuideModal) {
    var guide = sizeGuideModal && sizeGuideModal.querySelector('[data-size-guide-modern]');
    if (!guide || guide.classList.contains('is-ready')) return;
    var source = guide.querySelector('[data-size-guide-source]');
    var output = guide.querySelector('[data-size-guide-rendered]');
    var items = Array.prototype.slice.call(source.querySelectorAll('li'));
    if (!items.length) return;

    var rows = [];
    var headings = [];
    var englishGuide = guide.dataset.language === 'en';
    var headingTranslations = {
      Breite: guide.dataset.widthLabel,
      Länge: guide.dataset.lengthLabel,
      Brust: guide.dataset.chestLabel,
      Taille: guide.dataset.waistLabel,
      Hüfte: guide.dataset.hipLabel,
      Bein: guide.dataset.legLabel,
      Umfang: guide.dataset.circumferenceLabel
    };
    function localizeGuideHeading(heading) {
      if (!englishGuide) return heading;
      return headingTranslations[heading] || heading;
    }
    items.forEach(function (item) {
      var text = item.textContent.trim();
      var separator = text.indexOf(':');
      if (separator < 1) return;
      var row = { size: text.slice(0, separator).trim(), values: {} };
      text.slice(separator + 1).split('·').forEach(function (part) {
        var match = part.trim().match(/^(.+?)\s+(-?\d+(?:[.,]\d+)?)\s*([^\d\s]+)?$/);
        if (!match) return;
        var label = match[1].trim();
        if (headings.indexOf(label) === -1) headings.push(label);
        row.values[label] = match[2];
      });
      if (Object.keys(row.values).length) rows.push(row);
    });

    if (!rows.length || !headings.length) return;

    var tableWrap = document.createElement('div');
    tableWrap.className = 'size-guide-modern__table-wrap';
    tableWrap.setAttribute('tabindex', '0');
    var table = document.createElement('table');
    table.className = 'size-guide-modern__table';
    var thead = document.createElement('thead');
    var headerRow = document.createElement('tr');
    [guide.dataset.sizeLabel].concat(headings).forEach(function (heading, index) {
      var cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = localizeGuideHeading(heading);
      if (index > 0) {
        var unit = document.createElement('span');
        unit.textContent = guide.dataset.unitLabel;
        cell.appendChild(unit);
      }
      headerRow.appendChild(cell);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    var tbody = document.createElement('tbody');
    rows.forEach(function (row) {
      var tableRow = document.createElement('tr');
      var sizeCell = document.createElement('th');
      sizeCell.scope = 'row';
      sizeCell.textContent = row.size;
      tableRow.appendChild(sizeCell);
      headings.forEach(function (heading) {
        var valueCell = document.createElement('td');
        valueCell.textContent = row.values[heading] || '—';
        tableRow.appendChild(valueCell);
      });
      tbody.appendChild(tableRow);
    });
    table.appendChild(tbody);
    tableWrap.appendChild(table);

    var note = source.querySelector('p');
    if (note) {
      var noteBox = document.createElement('p');
      noteBox.className = 'size-guide-modern__note';
      var noteText = note.textContent.trim();
      if (englishGuide && noteText === 'Alle Maße in cm. Toleranz ± 3 cm.') {
        noteText = guide.dataset.noteLabel;
      }
      noteBox.textContent = noteText;
      output.appendChild(noteBox);
    }
    output.appendChild(tableWrap);
    output.hidden = false;
    source.hidden = true;
    guide.classList.add('is-ready');
  }

  function openSizeGuide() {
    var sizeGuideModal = getSizeGuideModal();
    if (!sizeGuideModal) return;
    sizeGuideTrigger = document.activeElement;
    buildSizeGuideTable(sizeGuideModal);
    clearTimeout(sizeGuideCloseTimer);
    sizeGuideModal.removeAttribute('hidden');
    sizeGuideModal.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () {
      sizeGuideModal.classList.add('is-open');
      var closeButton = sizeGuideModal.querySelector('[data-size-guide-close]');
      if (closeButton) closeButton.focus();
    });
    document.body.classList.add('drawer-open');
  }

  function closeSizeGuide() {
    var sizeGuideModal = getSizeGuideModal();
    if (!sizeGuideModal || !sizeGuideModal.classList.contains('is-open')) return;
    sizeGuideModal.classList.remove('is-open');
    sizeGuideModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
    sizeGuideCloseTimer = window.setTimeout(function () {
      if (!sizeGuideModal.classList.contains('is-open')) {
        if (sizeGuideTrigger) sizeGuideTrigger.focus();
      }
    }, 320);
  }

  document.addEventListener('click', function (e) {
    var openButton = e.target.closest('[data-size-guide-open]');
    if (openButton) {
      e.preventDefault();
      openSizeGuide();
      return;
    }
    if (e.target.closest('[data-size-guide-close]')) closeSizeGuide();
  });

  document.addEventListener('keydown', function (e) {
    var sizeGuideModal = getSizeGuideModal();
    if (e.key !== 'Escape') return;
    if (sizeGuideModal && sizeGuideModal.classList.contains('is-open')) {
      closeSizeGuide();
      return;
    }
    NE.closeAllDrawers();
  });

  /* -------------------------------------- localization + scroll to top */
  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-localization-select]')) return;
    if (!e.target.form) return;
    if (e.target.name === 'language_code') {
      var returnField = e.target.form.querySelector('input[name="return_to"]');
      var currentPath = window.location.pathname || '/';
      if (e.target.value === 'en' && currentPath !== '/en' && currentPath.indexOf('/en/') !== 0) {
        currentPath = '/en' + (currentPath === '/' ? '' : currentPath);
      }
      if (e.target.value !== 'en' && (currentPath === '/en' || currentPath.indexOf('/en/') === 0)) {
        currentPath = currentPath.replace(/^\/en(?=\/|$)/, '') || '/';
      }
      if (returnField) returnField.value = currentPath + window.location.search + window.location.hash;
    }
    if (typeof e.target.form.requestSubmit === 'function') e.target.form.requestSubmit();
    else e.target.form.submit();
  });

  var scrollTicking = false;
  function syncScrollTop() {
    var button = document.querySelector('[data-scroll-top]');
    if (!button) return;
    button.hidden = false;
    button.classList.toggle('is-visible', (window.scrollY || window.pageYOffset) > 700);
  }

  window.addEventListener('scroll', function () {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(function () {
      syncScrollTop();
      scrollTicking = false;
    });
  }, { passive: true });

  document.addEventListener('click', function (e) {
    var button = e.target.closest('[data-scroll-top]');
    if (!button) return;
    window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });

  document.addEventListener('shopify:section:load', syncScrollTop);
  syncScrollTop();

  document.documentElement.classList.add('theme-ready');
})();
